import type {
  PermissionRegion,
  SelectOption,
  VehicleOilRow,
  VehicleOwnership,
  VehicleRow,
  VehicleStatus,
} from "./types";
import type {
  VehicleConsumptionPayload,
  VehiclePayload,
} from "../../services/domains/vehicle";

export const VEHICLE_NEW_ROW_KEY = "vehicle-new-row";

export const VEHICLE_OWNERSHIP_OPTIONS: Array<{
  label: string;
  value: VehicleOwnership;
}> = [
  { label: "Đầu tư", value: "INVESTMENT" },
  { label: "Thuê khô", value: "DRY_RENTAL" },
];

export const VEHICLE_STATUS_OPTIONS: Array<{
  label: string;
  value: VehicleStatus;
}> = [
  { label: "Đang hoạt động", value: 1 },
  { label: "Ngừng hoạt động", value: 0 },
];

export const VEHICLE_CONSUMPTION_FIELDS: Array<{
  key: keyof VehicleConsumptionPayload;
  title: string;
}> = [
  { key: "equal0", title: "Tiêu hao 0 tấn" },
  { key: "from0to10", title: "Tiêu hao 0-10 tấn" },
  { key: "from10to15", title: "Tiêu hao 10-15 tấn" },
  { key: "from15to20", title: "Tiêu hao 15-20 tấn" },
  { key: "from20to25", title: "Tiêu hao 20-25 tấn" },
  { key: "moreThan25", title: "Tiêu hao >25 tấn" },
];

export const DEFAULT_CONSUMPTION: VehicleConsumptionPayload = {
  equal0: 0,
  from0to10: 0,
  from10to15: 0,
  from15to20: 0,
  from20to25: 0,
  moreThan25: 0,
};

export const getVehicleOwnershipLabel = (value?: string) =>
  VEHICLE_OWNERSHIP_OPTIONS.find((item) => item.value === value)?.label ??
  value ??
  "";

export const getVehicleStatusLabel = (value?: number | string) =>
  VEHICLE_STATUS_OPTIONS.find((item) => item.value === Number(value))?.label ??
  "";

const toNumber = (value: any) => {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) ? nextValue : 0;
};

const getRegionId = (region: PermissionRegion | any) =>
  String(region?._id ?? region?.id ?? region?.value ?? region?.region ?? "");

const getRegionName = (region: PermissionRegion | any) =>
  String(
    region?.name ??
      region?.title ??
      region?.label ??
      region?.regionName ??
      region?.code ??
      "",
  );

const normalizeConsumption = (consumption: any): VehicleConsumptionPayload => ({
  equal0: toNumber(consumption?.equal0),
  from0to10: toNumber(consumption?.from0to10),
  from10to15: toNumber(consumption?.from10to15),
  from15to20: toNumber(consumption?.from15to20),
  from20to25: toNumber(consumption?.from20to25),
  moreThan25: toNumber(consumption?.moreThan25),
});

export const normalizeTruckVendorOption = (truckVendor: any): SelectOption => ({
  value: String(truckVendor?.value ?? truckVendor?._id ?? truckVendor ?? ""),
  label: String(
    truckVendor?.label ??
      truckVendor?.shortName ??
      truckVendor?.name ??
      truckVendor ??
      "",
  ),
  record: truckVendor?.record ?? truckVendor,
});

export const normalizeVehicleOilRows = (
  oil: any[] = [],
  regions: PermissionRegion[] = [],
): VehicleOilRow[] => {
  const sourceRegions =
    regions.length > 0
      ? regions
      : oil.map((item) => ({
          _id: item.region,
          name: item.regionName,
        }));

  return sourceRegions.map((region) => {
    const regionId = getRegionId(region);
    const matchedOil = oil.find((item) => String(item?.region) === regionId);
    const regionName = matchedOil?.regionName ?? getRegionName(region);

    return {
      _id: matchedOil?._id,
      key: `${regionId || regionName}`,
      region: regionId,
      regionName,
      consumption: normalizeConsumption(matchedOil?.consumption),
    };
  });
};

export const normalizeVehicleRows = (
  vehicles: any[] = [],
  regions: PermissionRegion[] = [],
): VehicleRow[] =>
  vehicles.map((vehicle) => {
    const truckVendor = normalizeTruckVendorOption(vehicle?.truckVendor);
    const key = String(vehicle?._id ?? vehicle?.id);

    return {
      ...vehicle,
      _id: vehicle?._id ?? vehicle?.id,
      key,
      ownership: (vehicle?.ownership ?? "INVESTMENT") as VehicleOwnership,
      oilTankVolume: toNumber(vehicle?.oilTankVolume),
      status: Number(vehicle?.status ?? 1) as VehicleStatus,
      truckVendor,
      truckVendorName: truckVendor.label,
      licensePlate: vehicle?.licensePlate ?? "",
      oil: normalizeVehicleOilRows(vehicle?.oil ?? [], regions),
    };
  });

export const createEmptyVehicleRow = (
  regions: PermissionRegion[] = [],
): VehicleRow => ({
  _id: null,
  key: VEHICLE_NEW_ROW_KEY,
  state: "new",
  ownership: "INVESTMENT",
  oilTankVolume: 0,
  status: 1,
  truckVendor: { label: "", value: "" },
  truckVendorName: "",
  licensePlate: "",
  oil: normalizeVehicleOilRows([], regions),
});

export const buildVehiclePayload = (row: VehicleRow | any): VehiclePayload => {
  const truckVendor = normalizeTruckVendorOption(row.truckVendor);

  return {
    ownership: row.ownership,
    oilTankVolume: toNumber(row.oilTankVolume),
    status: Number(row.status ?? 1),
    truckVendor: truckVendor.value,
    licensePlate: row.licensePlate ?? "",
    oil: (row.oil ?? []).map((item: VehicleOilRow) => ({
      region: item.region,
      consumption: normalizeConsumption(item.consumption),
    })),
  };
};

export const hasVehicleRequiredFields = (row: VehicleRow) => {
  const payload = buildVehiclePayload(row);
  return Boolean(
    payload.ownership &&
    payload.truckVendor &&
    payload.licensePlate &&
    payload.oil.length > 0,
  );
};

export const runVehicleRequest = async <T>(
  request: () => Promise<T>,
  notifyError: (message: string) => void,
  fallbackMessage: string,
): Promise<T | undefined> => {
  try {
    return await request();
  } catch (error: any) {
    notifyError(error?.message || fallbackMessage);
    return undefined;
  }
};
